import { StatusPill } from "@/components/portfolio/StatusPill";
import { SummaryMetricGrid } from "@/components/ui/SummaryGrid";
import { Panel } from "@/components/ui/Panel";
import {
  getEstateTypeSingular,
  getWatchDogIcon,
} from "@/components/hlidaci-pes/watchdog-page/watchDogOverviewUtils";
import { useLanguage } from "@/context/LanguageContext";
import type { ClientEstate, EstateStats } from "@/types";
import { formatCZK } from "@/utils/MyEstateHelper";
import { ArrowRightIcon } from "@heroicons/react/24/outline";

type PortfolioEstateCardProps = {
  estate: ClientEstate;
  stats?: EstateStats;
  isPredictionView: boolean;
  onSelect: () => void;
};

export function EstateStatusBadge({ status }: { status: string }) {
  const { language } = useLanguage();
  const copy = {
    cs: { rented: "Pronajato", vacant: "Neobsazeno" },
    en: { rented: "Rented", vacant: "Vacant" },
  }[language];
  const isRented = status.includes("RENTED");
  const isFree = status.includes("VACANT");

  return (
    <StatusPill tone={isRented ? "success" : isFree ? "warning" : "neutral"}>
      {isRented ? copy.rented : isFree ? copy.vacant : status}
    </StatusPill>
  );
}

export function PortfolioEstateCard({
  estate,
  stats,
  isPredictionView,
  onSelect,
}: PortfolioEstateCardProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      noAddress: "Bez adresy",
      open: "Otevřít nemovitost",
      acquisitionValue: "Pořizovací hodnota",
      income: "Příjmy",
      expense: "Výdaje",
      result: "Výsledek",
      houses: "Dům",
      flats: "Byt",
      land: "Pozemek",
    },
    en: {
      noAddress: "No address",
      open: "Open property",
      acquisitionValue: "Acquisition value",
      income: "Income",
      expense: "Expenses",
      result: "Result",
      houses: "House",
      flats: "Apartment",
      land: "Land",
    },
  }[language];
  const income = stats?.currentYearStats.income ?? 0;
  const expense = stats?.currentYearStats.expense ?? 0;
  const currentProfit = stats?.currentYearStats.profit ?? 0;
  const projectedProfit = stats?.currentYearStats.profitTotal ?? 0;
  const result = isPredictionView ? projectedProfit : currentProfit;
  const Icon = getWatchDogIcon(estate.estateType);
  const estateType = getEstateTypeSingular(estate.estateType, copy);
  const metrics = [
    {
      label: copy.acquisitionValue,
      value: formatCZK(estate.purchasePrice ?? 0),
    },
    { label: copy.income, value: formatCZK(income) },
    { label: copy.expense, value: formatCZK(expense) },
    {
      label: copy.result,
      value: formatCZK(result),
      tone: result < 0 ? ("negative" as const) : ("default" as const),
    },
  ];

  return (
    <Panel
      as="button"
      type="button"
      onClick={onSelect}
      className="group min-w-0 overflow-hidden p-0 text-left transition-colors hover:border-softiq-primary/20 hover:bg-white dark:hover:border-white/20 dark:hover:bg-white/[0.07]"
      aria-label={`${copy.open}: ${estate.name}`}
    >
      <div className="flex items-start gap-3 px-4 py-4 sm:px-5">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-softiq-secondary-shade-100 text-softiq-primary dark:bg-white/10 dark:text-softiq-secondary">
          <Icon aria-hidden="true" className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="min-w-0 truncate text-sm font-medium">
              {estate.name}
            </p>
            <EstateStatusBadge status={estate.status} />
          </div>
          <p className="mt-1 truncate text-[11px] text-softiq-primary/50 dark:text-white/50">
            {estate.address || copy.noAddress}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[
              estateType,
              estate.flatCategory,
              estate.usableArea ? `${estate.usableArea} m²` : null,
            ]
              .filter((value): value is string => Boolean(value))
              .map((value) => (
                <span key={value} className="app-pill-property">
                  {value}
                </span>
              ))}
          </div>
        </div>
        <ArrowRightIcon
          aria-hidden="true"
          className="mt-1 size-4 shrink-0 text-softiq-primary/30 transition-transform group-hover:translate-x-0.5 group-hover:text-softiq-primary dark:text-white/30 dark:group-hover:text-white"
        />
      </div>

      <SummaryMetricGrid metrics={metrics} layout="compact" />
    </Panel>
  );
}
